import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/app/store';
import { CatalogueState, Category, Subcategory } from './categories.interface';

// Thunks para Usuarios
import * as thunks from './categoriesThunks';
import { APIResponse } from '@/types/custom-http';


const initialState: CatalogueState = {
  categories: [],
  selectedCategory: null,
  subcategories: [],
  selectedSubcategory: null,
  loading: false,
  error: null,
};



// Slice
const catalogueSlice = createSlice({
  name: 'catalogue',
  initialState,
  reducers: {
    clearCategories: (state) => {
      state.categories = [];
    },
    clearSelectedCategory: (state) => {
      state.selectedCategory = null;
    },
    clearSubcategories: (state) => {
      state.subcategories = [];
    },
    clearSelectedSubcategory: (state) => {
      state.selectedSubcategory = null;
    },
    clearCatalogueError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Obtener todas las categorías
      .addCase(thunks.fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchCategories.fulfilled, (state, action: PayloadAction<APIResponse<Category[]>>) => {
        state.loading = false;
        if(action.payload.data){
          state.categories = action.payload.data;
        }
      })
      .addCase(thunks.fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Obtener una categoría específico por ID
      .addCase(thunks.fetchCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchCategoryById.fulfilled, (state, action: PayloadAction<APIResponse<Category>>) => {
        state.loading = false;
        if(action.payload.data){
          state.selectedCategory = action.payload.data;
        }
      })
      .addCase(thunks.fetchCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Crear una nueva categoría
      .addCase(thunks.createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createCategory.fulfilled, (state, action: PayloadAction<APIResponse<Category>>) => {
        state.loading = false;
        if(action.payload.data){
          state.categories.push(action.payload.data);
        }
      })
      .addCase(thunks.createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Actualizar datos de una categoría
      .addCase(thunks.updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateCategory.fulfilled, (state, action: PayloadAction<APIResponse<Category>>) => {
        state.loading = false;
        const index = state.categories.findIndex(item => item.id === action.payload.data?.id);
        if (index !== -1 && action.payload.data) {
          state.categories[index] = action.payload.data;
        }
        if (state.selectedCategory && state.selectedCategory.id === action.payload.data?.id) {
          state.selectedCategory = action.payload.data;
        }
      })
      .addCase(thunks.updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Eliminar una categoría
      .addCase(thunks.deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        const deletedCategoryId = action.payload;
        
        state.categories = state.categories.filter(item => item.id !== deletedCategoryId);
        
        if (state.selectedCategory && state.selectedCategory.id === deletedCategoryId) {
          state.selectedCategory = null;
        }
      })
      .addCase(thunks.deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })


      // Obtener todas las subcategorías
      .addCase(thunks.fetchSubategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchSubategories.fulfilled, (state, action: PayloadAction<APIResponse<Subcategory[]>>) => {
        state.loading = false;
        if(action.payload.data){
          state.subcategories = action.payload.data;
        }
      })
      .addCase(thunks.fetchSubategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Obtener una subcategoría específico por ID
      .addCase(thunks.fetchSubcategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchSubcategoryById.fulfilled, (state, action: PayloadAction<APIResponse<Subcategory>>) => {
        state.loading = false;
        if(action.payload.data){
          state.selectedSubcategory = action.payload.data;
        }
      })
      .addCase(thunks.fetchSubcategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Crear una nueva subcategoría
      .addCase(thunks.createSubcategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createSubcategory.fulfilled, (state, action: PayloadAction<APIResponse<Subcategory>>) => {
        state.loading = false;
        if(action.payload.data){
          state.subcategories.push(action.payload.data);
        }
      })
      .addCase(thunks.createSubcategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Actualizar datos de una subcategoría
      .addCase(thunks.updateSubcategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateSubcategory.fulfilled, (state, action: PayloadAction<APIResponse<Subcategory>>) => {
        state.loading = false;
        const index = state.subcategories.findIndex(item => item.id === action.payload.data?.id);
        if (index !== -1 && action.payload.data) {
          state.subcategories[index] = action.payload.data;
        }
        if (state.selectedSubcategory && state.selectedSubcategory.id === action.payload.data?.id) {
          state.selectedSubcategory = action.payload.data;
        }
      })
      .addCase(thunks.updateSubcategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Eliminar una subcategoría
      .addCase(thunks.deleteSubcategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.deleteSubcategory.fulfilled, (state, action) => {
        state.loading = false;
        const deletedSubcategoryrId = action.payload;
        
        state.subcategories = state.subcategories.filter(item => item.id !== deletedSubcategoryrId);
        
        if (state.selectedSubcategory && state.selectedSubcategory.id === deletedSubcategoryrId) {
          state.selectedSubcategory = null;
        }
      })
      .addCase(thunks.deleteSubcategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })
      
  },
});

// Selectors
export const selectAllCategories = (state: RootState) => state.catalogue.categories;
export const selectCategoryById = (state: RootState, id: number) => state.catalogue.categories.find(item => item.id === id);
export const selectSelectedCategory = (state: RootState) => state.catalogue.selectedCategory;

export const selectAllSubcategories = (state: RootState) => state.catalogue.subcategories;
export const selectSubcategoryById = (state: RootState, id: number) => state.catalogue.subcategories.find(item => item.id === id);
export const selectSelectedSubcategory = (state: RootState) => state.catalogue.selectedSubcategory;

export const selectProductLoading = (state: RootState) => state.catalogue.loading;
export const selectProductError = (state: RootState) => state.catalogue.error;

// Actions
export const { clearCategories, clearSubcategories, clearSelectedCategory, clearSelectedSubcategory,  clearCatalogueError} = catalogueSlice.actions;

export default catalogueSlice.reducer;